import { FunctionComponent, lazy, Suspense, useEffect } from "react";
import { useLocation } from "react-router-dom";

import { ROUTE_CONFIG } from "@/routes/config";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";

import { ErrorBoundary } from "@/components/ErrorBoundary/ErrorBoundary";
import { TourReadyBar } from "@/components/TourReadyBar/TourReadyBar";

import {
	openTour,
	selectKbHandoffActive,
	selectIsTourOpen,
} from "@/pages/Private/redux/onboardingTour/onboardingTour.slice";
import { TourStepId } from "@/pages/Private/redux/onboardingTour/schema";

// Lazy so the setup-guide tour (PREC-3700) doesn't weigh down the layout bundles.
// A lazy chunk can fail to load (a tab left open across a deploy asks for a name
// the server no longer has), so each sits in a boundary that renders nothing:
// the tour is optional and must never replace the page (PREC-4387).
const OnboardingTourModal = lazy(() => import("./OnboardingTourModal"));
const LeadListTour = lazy(() =>
	import("./components/leadListTour/LeadListTour").then((m) => ({ default: m.LeadListTour }))
);

/**
 * Reopens the configuration modal when the user leaves the Knowledge DB tour
 * hand-off page via the left nav instead of the return bar (PREC-3745). While
 * the KB page's `?tour=1` return bar is mounted it sets `kbHandoffActive`; the
 * moment the route moves off that page with the tour still closed, we reopen it
 * on the KNOWLEDGE step (openTour clears the flag).
 */
export const useReopenTourOnNavAway = () => {
	const dispatch = useAppDispatch();
	const location = useLocation();
	const handoffActive = useAppSelector(selectKbHandoffActive);
	const isOpen = useAppSelector(selectIsTourOpen);

	useEffect(() => {
		const onKnowledgeDb = location.pathname === ROUTE_CONFIG.KNOWLEDGE_DB;

		if (handoffActive && !isOpen && !onKnowledgeDb) {
			dispatch(openTour(TourStepId.KNOWLEDGE));
		}
	}, [handoffActive, isOpen, location.pathname, dispatch]);
};

/**
 * Mounts the setup-guide tour surfaces (PREC-3700) — the full-screen tour modal
 * and the step-ready bar — for an app layout. Every layout a signed-in user can
 * land on must render this once; the dashboard is the tour's primary surface
 * (auto-open + resume banner), so leaving it out of a layout silently breaks
 * every tour entry point on that route.
 */
export const OnboardingTourHost: FunctionComponent = () => {
	useReopenTourOnNavAway();

	return (
		<>
			<ErrorBoundary fallback={null}>
				<Suspense fallback={null}>
					<OnboardingTourModal />
				</Suspense>
			</ErrorBoundary>
			<ErrorBoundary fallback={null}>
				<Suspense fallback={null}>
					<LeadListTour />
				</Suspense>
			</ErrorBoundary>
			<TourReadyBar />
		</>
	);
};
